import axios from 'axios';
import { API_BASE_URL } from '../constants/domain';
import { useAuth } from '@/zustand/auth-store';
import { getSessionTokenFromUrl } from '../utilis/session-token';
// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
import { useNetworkDiagnostics } from '../zustand/network-diagnostics-store';

const ApiInstance = axios.create({
  baseURL: API_BASE_URL,
});

ApiInstance.interceptors.request.use(
  config => {
    // Diagnostics only — stamped before the skipAuth branch so every request
    // gets timed regardless of which auth path it takes below. Reads the
    // store directly (getState(), not a hook) since this file has no
    // component lifecycle.
    config.metadata = { requestStartedAt: performance.now() };

    if (config.skipAuth) {
      // If skipAuth is true, don't add the token
      return config;
    }

    // The guest exercise flow's own session_token (?session_token= on the
    // URL, carried from /camera-permission through /try-demo and /exercise)
    // identifies this specific prescription session — it must win over a
    // clinician accessToken that happens to be sitting in the same browser.
    const sessionToken = getSessionTokenFromUrl();
    if (sessionToken) {
      config.headers.Authorization = `Bearer ${sessionToken}`;
      return config;
    }

    const accessToken = useAuth.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    }

    const legacyToken = localStorage.getItem('rehab-auth');
    const parsedLegacyToken = legacyToken && JSON.parse(legacyToken);

    if (parsedLegacyToken && parsedLegacyToken.state.token) {
      config.headers.Authorization = `Bearer ${parsedLegacyToken.state.token}`;
    }

    return config;
  },
  error => {
    console.error('Request error', error);
    return Promise.reject(error);
  }
);

// Diagnostics only. `performance.now()` deltas, `JSON.stringify(...).length`
// approximations, and `content-length` where the server sends it — see
// network-diagnostics-store for why this never throws into the caller.
function recordRestDiagnostics(config, { status, error, responseData }) {
  try {
    const startedAt = config?.metadata?.requestStartedAt;
    if (typeof startedAt !== 'number') {
      return;
    }
    const durationMs = Math.round(performance.now() - startedAt);
    const endpoint = config?.url ?? null;
    const record = useNetworkDiagnostics.getState().record;

    record('rest', 'request_duration', durationMs, 'ms', { endpoint });

    if (config?.data) {
      try {
        const payload = typeof config.data === 'string' ? config.data : JSON.stringify(config.data);
        record('rest', 'request_payload_bytes', payload.length, 'bytes', { endpoint });
      } catch (_error) {
        // FormData / non-serializable bodies (multipart uploads) — size not
        // measurable this way, skipped rather than guessed.
      }
    }

    if (responseData !== undefined) {
      try {
        record('rest', 'response_payload_bytes', JSON.stringify(responseData).length, 'bytes', {
          endpoint,
        });
      } catch (_error) {
        // Non-JSON response body — skipped.
      }
    }

    if (error) {
      record('rest', 'request_error', status ?? 0, 'http_status', { endpoint });
    }
  } catch (_error) {
    // Never let instrumentation break a real request.
  }
}

ApiInstance.interceptors.response.use(
  response => {
    recordRestDiagnostics(response.config, {
      status: response.status,
      error: false,
      responseData: response.data,
    });
    return response;
  },
  error => {
    recordRestDiagnostics(error.config, {
      status: error.response?.status,
      error: true,
      responseData: undefined,
    });

    // Some endpoints return 401 for reasons that have nothing to do with the
    // patient's own session — the realtime token endpoint reports OpenAI
    // rejecting the *backend's* key as a 401. Bouncing those to /login would
    // end a live exercise over a third-party credential problem.
    const skipAuthRedirect = error.config?.skipAuthRedirect;

    if (error.response && error.response.status === 401 && !skipAuthRedirect) {
      // Remove auth token from localStorage when receiving 401
      localStorage.removeItem('rehab-auth');
      // Navigate to login page
      window.location.href = '/login';
    }
    console.error('Response error', error);
    return Promise.reject(error);
  }
);

export default ApiInstance;
